import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, Upload, Plus, Download, KeyRound, Power, X } from "lucide-react";
import api, { formatApiError, API } from "@/lib/api";
import { AdminLayout } from "@/components/AdminLayout";

export default function AdminEmployees() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const load = (q = "") => api.get("/admin/employees", { params: { search: q } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const doImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/employees/import", fd);
      setImportResult(data);
      toast.success(`Imported ${data.created} employees`);
      load(search);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("wp_token");
    const res = await fetch(`${API}/admin/employees/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "employee_import_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const toggle = async (emp) => {
    try {
      await api.patch(`/admin/employees/${emp.id}/status`, { is_active: !emp.is_active });
      load(search);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resetPw = async (emp) => {
    try {
      const { data } = await api.post(`/admin/employees/${emp.id}/reset-password`);
      toast.success(data.message);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="overline mb-2">Directory</div>
            <h1 className="font-display font-black text-4xl tracking-tight">Employees</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={downloadTemplate} data-testid="download-template" className="flex items-center gap-2 border border-[#E4E4E7] hover:border-[#0090A8] hover:text-[#0090A8] rounded-sm px-4 py-2 text-sm transition-colors">
              <Download size={16} /> Template
            </button>
            <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={doImport} data-testid="import-file-input" />
            <button onClick={() => fileRef.current?.click()} data-testid="import-btn" className="flex items-center gap-2 border border-[#E4E4E7] hover:border-[#0090A8] hover:text-[#0090A8] rounded-sm px-4 py-2 text-sm transition-colors">
              <Upload size={16} /> Import Excel
            </button>
            <button onClick={() => setShowAdd(true)} data-testid="add-employee-btn" className="flex items-center gap-2 bg-[#0090A8] text-black hover:bg-[#00728A] font-bold rounded-sm px-4 py-2 text-sm transition-colors">
              <Plus size={16} /> Add
            </button>
          </div>
        </div>

        {importResult && (
          <div className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-4 mb-6" data-testid="import-summary">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-sm">Import Summary</h3>
              <button onClick={() => setImportResult(null)}><X size={16} className="text-gray-500" /></button>
            </div>
            <p className="text-sm text-gray-400">
              <span className="text-[#12805C] font-bold">{importResult.created} created</span> · <span className="text-[#D92D20] font-bold">{importResult.skipped_count} skipped</span>
            </p>
            {importResult.skipped?.length > 0 && (
              <ul className="mt-2 text-xs text-gray-500 space-y-0.5 max-h-32 overflow-y-auto">
                {importResult.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.reason} {s.emp_id ? `(${s.emp_id})` : ""}</li>)}
              </ul>
            )}
          </div>
        )}

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
          <input data-testid="employee-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, ID, email, department…"
            className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm pl-10 pr-4 py-2.5 outline-none transition-colors" />
        </div>

        <div className="border border-[#E4E4E7] rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E4E4E7] text-left overline">
                <th className="px-4 py-3">Emp ID</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3 hidden md:table-cell">Email</th>
                <th className="px-4 py-3 hidden lg:table-cell">Department</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-600">No employees found. Import an Excel file or add one.</td></tr>
              ) : rows.map((e) => (
                <tr key={e.id} data-testid={`employee-row-${e.id}`} className="border-b border-[#E4E4E7] last:border-0 hover:bg-black/5 transition-colors">
                  <td className="px-4 py-3 font-mono2 text-gray-400">{e.emp_id}</td>
                  <td className="px-4 py-3 font-medium">{e.name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-400">{e.email}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-400">{e.department || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[0.65rem] font-bold uppercase px-2 py-0.5 rounded-sm border ${e.is_active ? "text-[#12805C] border-[#12805C]/30 bg-[#12805C]/10" : "text-gray-500 border-black/10 bg-black/5"}`}>
                      {e.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => resetPw(e)} data-testid={`reset-pw-${e.id}`} title="Reset password to Employee ID" className="p-1.5 text-gray-500 hover:text-[#0090A8] transition-colors"><KeyRound size={16} /></button>
                      <button onClick={() => toggle(e)} data-testid={`toggle-${e.id}`} title={e.is_active ? "Deactivate" : "Activate"} className={`p-1.5 transition-colors ${e.is_active ? "text-gray-500 hover:text-[#D92D20]" : "text-gray-500 hover:text-[#12805C]"}`}><Power size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && <AddEmployeeModal onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(search); }} />}
    </AdminLayout>
  );
}

function AddEmployeeModal({ onClose, onDone }) {
  const [form, setForm] = useState({ emp_id: "", name: "", email: "", department: "", designation: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/employees", form);
      toast.success("Employee added. Initial password = Employee ID.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const fields = [
    { k: "emp_id", label: "Employee ID", required: true, wide: false },
    { k: "name", label: "Full Name", required: true, wide: false },
    { k: "email", label: "Email", required: true, wide: true, type: "email" },
    { k: "department", label: "Department", wide: false },
    { k: "designation", label: "Designation", wide: false },
    { k: "phone", label: "Phone", wide: true },
  ];

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="add-employee-modal"
        className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm p-6 w-full max-w-md fade-up">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display font-bold text-xl">Add Employee</h2>
          <button type="button" onClick={onClose}><X size={20} className="text-gray-500" /></button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {fields.map((f) => (
            <div key={f.k} className={f.wide ? "col-span-2" : ""}>
              <label className="overline block mb-1.5">{f.label}{f.required && " *"}</label>
              <input data-testid={`add-${f.k}`} type={f.type || "text"} value={form[f.k]} onChange={set(f.k)} required={f.required}
                className="w-full bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2 outline-none transition-colors" />
            </div>
          ))}
        </div>
        <button data-testid="submit-add-employee" disabled={loading}
          className="w-full mt-6 bg-[#0090A8] text-black hover:bg-[#00728A] font-bold rounded-sm py-2.5 transition-colors disabled:opacity-50">
          {loading ? "Adding…" : "Add Employee"}
        </button>
      </form>
    </div>
  );
}
